'use client';

import { Deployment, Project } from '../../../types';
import { useEffect, useRef, useState } from 'react';

import PreviewImage from './PreviewImage';
import StatusPill from './StatusPill';
import { cn } from '../../lib/utils';
import { projectService } from '../../services/ProjectService';
import { toast } from 'react-hot-toast';
import { useAuth } from '@clerk/clerk-react';

interface ProjectCardProps {
    project: Project;
    className?: string;
    onItemClick?: (path: string, type: string) => void;
}

const getDeploymentUrl = (deployment: Deployment) => {
    return deployment.custom_domain ? `${deployment.custom_domain}` : deployment.service_url;
};

// Context Menu Component
const ProjectContextMenu = ({
    isPublished,
    latestDeployment,
    onClose,
    onEmbed,
    onOpen,
    onRename,
    onDuplicate,
    onDelete
}: {
    isPublished: boolean;
    latestDeployment: Deployment | null;
    onClose: () => void;
    onEmbed: (e: React.MouseEvent) => void;
    onOpen: (e: React.MouseEvent) => void;
    onRename: (e: React.MouseEvent) => void;
    onDuplicate: (e: React.MouseEvent) => void;
    onDelete: (e: React.MouseEvent) => void;
}) => (
    <div
        className="absolute z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px]"
        style={{
            top: '0px',
            right: '0px'
        }}
    >
        {isPublished ? (
            <>
                {latestDeployment && (
                    <a
                        href={getDeploymentUrl(latestDeployment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                    >
                        Visit Project
                    </a>
                )}
                <button
                    onClick={onEmbed}
                    className="w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100 transition-colors"
                >
                    Embed Project
                </button>
            </>
        ) : (
            <>
                <button
                    onClick={onOpen}
                    className="w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100 transition-colors"
                >
                    Open Project
                </button>
                <button
                    onClick={onRename}
                    className="w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100 transition-colors"
                >
                    Rename Project
                </button>
            </>
        )}
        <button
            onClick={onDuplicate}
            className="w-full px-4 py-2 text-xs text-left text-gray-700 hover:bg-gray-100 transition-colors"
        >
            Duplicate Project
        </button>
        <div className="h-px bg-gray-200 my-1" />
        <button
            onClick={onDelete}
            className="w-full px-4 py-2 text-xs text-left text-red-600 hover:bg-gray-100 transition-colors"
        >
            Delete Project
        </button>
    </div>
);

// Delete Confirmation Dialog Component
const DeleteConfirmDialog = ({
    isDeleting,
    onCancel,
    onConfirm
}: {
    isDeleting: boolean;
    onCancel: (e: React.MouseEvent) => void;
    onConfirm: (e: React.MouseEvent) => void;
}) => (
    <div className="absolute inset-0 bg-white rounded-lg flex items-center justify-center p-4 z-10 border border-gray-200">
        <div className="text-center">
            <p className="mb-4 text-sm text-foreground">Are you sure you want to delete this project?</p>
            <div className="flex space-x-3 justify-center">
                <button
                    onClick={onCancel}
                    className={cn(
                        "px-4 py-2 text-xs font-medium",
                        "text-foreground bg-secondary rounded-md",
                        "hover:bg-secondary/80 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30",
                        isDeleting && "opacity-50 cursor-not-allowed"
                    )}
                    disabled={isDeleting}
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    className={cn(
                        "px-4 py-2 text-xs font-medium",
                        "text-destructive-foreground bg-destructive rounded-md",
                        "hover:bg-destructive/90 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30",
                        isDeleting && "opacity-50 cursor-not-allowed"
                    )}
                    disabled={isDeleting}
                >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
            </div>
        </div>
    </div>
);

export default function ProjectCard({ project, className = '', onItemClick }: ProjectCardProps) {
    // Group related state together
    const [projectName, setProjectName] = useState(project.name);
    const [isEditing, setIsEditing] = useState(false);

    const [showContextMenu, setShowContextMenu] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const { getToken } = useAuth();

    // Refs
    const cardRef = useRef<HTMLDivElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!project.onDelete) return;

        try {
            setIsDeleting(true);
            await project.onDelete(project.id);
        } catch (error) {
            console.error('Error deleting project:', error);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
            setShowContextMenu(false);
        }
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowContextMenu(true);
    };

    const handleEmbed = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (latestDeployment) {
            const embedCode = `<iframe src="${getDeploymentUrl(latestDeployment)}" width="100%" height="600" frameborder="0"></iframe>`;
            navigator.clipboard.writeText(embedCode).then(() => {
                toast.success('Embed code copied to clipboard');
            }).catch(() => {
                toast.error('Failed to copy embed code');
            });
        }
        setShowContextMenu(false);
    };

    const handleOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onItemClick) {
            onItemClick(project.id, 'project');
        }
        setShowContextMenu(false);
    };

    const handleRename = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsEditing(true);
        setShowContextMenu(false);
        // Focus the input after the state update
        setTimeout(() => {
            nameInputRef.current?.focus();
        }, 0);
    };

    const handleDuplicate = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (project.onDuplicate) {
            project.onDuplicate(project.id);
        }
        setShowContextMenu(false);
    };

    const handleNameSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (projectName.trim() && projectName !== project.name) {
            setIsRenaming(true);
            try {
                const token = await getToken();
                if (!token) {
                    throw new Error("Authentication token not available");
                }
                projectService.setToken(token);

                // Update project through API
                await projectService.updateProject(project.id, { name: projectName });

                // Update the project name in the UI
                project.name = projectName;

                toast.success('Project renamed successfully');
            } catch (error) {
                console.error('Error renaming project:', error);
                toast.error('Failed to rename project');
                setProjectName(project.name); // Revert on error
            } finally {
                setIsRenaming(false);
                setIsEditing(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
                setShowContextMenu(false);
                if (isEditing) {
                    setIsEditing(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isEditing]);

    const getLatestDeployment = () => {
        if (!project.deployments || project.deployments.length === 0) return null;
        return project.deployments.reduce((latest, current) => {
            return new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest;
        });
    };

    const latestDeployment = getLatestDeployment();

    return (
        <div
            ref={cardRef}
            className={cn(
                "border border-gray-200 rounded-lg p-3 sm:p-4 bg-background min-h-[200px] flex flex-col w-full",
                className
            )}
            onContextMenu={handleContextMenu}
        >
            {/* Header Section */}
            <div className="-mx-3 -mt-3 sm:-mx-4 sm:-mt-4 relative">
                <div className="w-full h-24 sm:h-32 flex items-center justify-center overflow-hidden rounded-t-lg pointer-events-none outline-none border-none">
                    <PreviewImage seed={project.id} />
                </div>
                {project.isPublished && latestDeployment && latestDeployment.service_url && (
                    <a
                        href={getDeploymentUrl(latestDeployment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors bg-white/80 rounded-md"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                    </a>
                )}
            </div>

            {/* Status Section */}
            <div className="flex flex-col mt-auto">
                <div className="h-px bg-gray-200 -mx-3 sm:-mx-4" />
                <div className="flex items-center justify-between pt-2">
                    <div className="flex flex-col">
                        {isEditing ? (
                            <form onSubmit={handleNameSubmit} className="flex items-center gap-2">
                                <input
                                    ref={nameInputRef}
                                    type="text"
                                    value={projectName}
                                    onChange={(e) => setProjectName(e.target.value)}
                                    className={cn(
                                        "text-sm font-medium text-foreground bg-transparent border-b border-gray-300 focus:border-primary focus:outline-none",
                                        isRenaming && "opacity-50 cursor-not-allowed"
                                    )}
                                    disabled={isRenaming}
                                    onBlur={handleNameSubmit}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </form>
                        ) : (
                            <h3 className="text-sm font-medium text-foreground truncate max-w-[150px] sm:max-w-[200px]">{project.name}</h3>
                        )}
                        <div>
                            <StatusPill isPublished={project.isPublished} deployment={latestDeployment} />
                        </div>
                    </div>
                    <div className="flex flex-col items-end">
                        <div className="text-xs text-gray-500">
                            {project.time && new Date(project.time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }).replace(',', '')}
                        </div>
                        <p className="text-xs text-gray-500">ID: {project.id}</p>
                    </div>
                </div>
            </div>

            {/* Context Menu */}
            {showContextMenu && (
                <ProjectContextMenu
                    isPublished={project.isPublished ?? false}
                    latestDeployment={latestDeployment}
                    onClose={() => setShowContextMenu(false)}
                    onEmbed={handleEmbed}
                    onOpen={handleOpen}
                    onRename={handleRename}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                />
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
                <DeleteConfirmDialog
                    isDeleting={isDeleting}
                    onCancel={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(false);
                    }}
                    onConfirm={handleDelete}
                />
            )}
        </div>
    );
}